import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedin, faGithub } from '@fortawesome/free-brands-svg-icons';
import { faArrowDown, faBars, faXmark } from '@fortawesome/free-solid-svg-icons';
import { identity } from '../data/content';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

const LINKS = [
  { id: 'story', label: 'Story' },
  { id: 'how-i-work', label: 'How I Work' },
  { id: 'projects', label: 'Projects' },
  { id: 'journey', label: 'Journey' },
  { id: 'contact', label: 'Contact' },
];

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState('story');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const sections = LINKS.map((l) => document.getElementById(l.id)).filter(Boolean);
    if (!sections.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: '-40% 0px -50% 0px', threshold: 0 }
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  return (
    <>
      <header
        className={cn(
          'fixed inset-x-0 top-0 z-[100] border-b border-transparent py-4 transition-all duration-300',
          scrolled && 'border-border bg-background/75 py-2.5 backdrop-blur-xl'
        )}
      >
        <div className="container flex items-center justify-between gap-4">
          <a href="#top" className="font-display text-xl font-bold tracking-tight" aria-label="Back to top">
            SJ<span className="text-primary-light">.</span>
          </a>

          <nav className="hidden items-center gap-7 md:flex" aria-label="Section navigation">
            {LINKS.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                className={cn(
                  'relative py-1 text-sm text-muted transition-colors hover:text-foreground',
                  active === link.id && 'text-foreground'
                )}
              >
                {link.label}
                {active === link.id && (
                  <span className="absolute -bottom-0.5 left-0 h-px w-full bg-primary-light" />
                )}
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-4 md:flex">
            <a
              href={identity.social.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted transition-colors hover:text-primary-light"
              aria-label="LinkedIn"
            >
              <FontAwesomeIcon icon={faLinkedin} />
            </a>
            <a
              href={identity.social.github}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted transition-colors hover:text-primary-light"
              aria-label="GitHub"
            >
              <FontAwesomeIcon icon={faGithub} />
            </a>
            <Button asChild size="sm" variant="outline">
              <a href="#contact">
                Let's talk <FontAwesomeIcon icon={faArrowDown} className="text-xs" />
              </a>
            </Button>
          </div>

          <button
            className="relative z-[210] text-xl text-foreground md:hidden"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <FontAwesomeIcon icon={menuOpen ? faXmark : faBars} />
          </button>
        </div>
      </header>

      <div
        className={cn(
          'fixed inset-0 z-[150] flex flex-col items-start justify-center gap-3 bg-background px-6 opacity-0 transition-opacity duration-300 pointer-events-none',
          menuOpen && 'opacity-100 pointer-events-auto'
        )}
      >
        {LINKS.map((link, i) => (
          <a
            key={link.id}
            href={`#${link.id}`}
            className={cn(
              'font-display text-4xl font-semibold text-foreground opacity-0 translate-y-3 transition-all duration-300'
            )}
            style={{
              transitionDelay: menuOpen ? `${i * 40}ms` : '0ms',
              opacity: menuOpen ? 1 : 0,
              transform: menuOpen ? 'translateY(0)' : 'translateY(12px)',
            }}
            onClick={closeMenu}
          >
            {link.label}
          </a>
        ))}
        <div className="mt-8 flex gap-4 font-mono text-sm text-muted">
          <a href={identity.social.linkedin} target="_blank" rel="noopener noreferrer">LinkedIn</a>
          <a href={identity.social.github} target="_blank" rel="noopener noreferrer">GitHub</a>
          <a href={`mailto:${identity.email}`}>Email</a>
        </div>
      </div>
    </>
  );
};

export default Nav;
